import React, { useEffect, useState, useCallback } from "react"
import { Link, useNavigate, useLocation } from "react-router-dom"
import { useAuth } from "../../context/AuthContext"
import API from "../../api/axios"
import "./dashboard.css"

/* ── Sidebar nav items ── */
const navItems = [
  { key: "dashboard",      icon: "fa-th-large",      label: "Dashboard"      },
  { key: "live-sessions",  icon: "fa-video",          label: "My Live Sessions"},
  { key: "profile",        icon: "fa-user-edit",      label: "Edit Profile"   },
  { key: "payments",       icon: "fa-credit-card",    label: "My Payments"    },
]

/* ── Sidebar ── */
const Sidebar = ({ user, active, setActive, onLogout }) => {
  const initials = user?.name?.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2) || "U"
  return (
    <div className="db-sidebar">
      <div className="db-sidebar-logo">
        <img src="/images/logo.png" alt="Rovers Access" />
        <span>Student Portal</span>
      </div>
      <nav className="db-nav">
        {navItems.map(item => (
          <button
            key={item.key}
            className={`db-nav-btn ${active === item.key ? "active" : ""}`}
            onClick={() => setActive(item.key)}
          >
            <i className={`fa ${item.icon}`}></i> {item.label}
          </button>
        ))}
        <Link to="/courses" className="db-nav-btn">
          <i className="fa fa-graduation-cap"></i> Browse Courses
        </Link>
        <Link to="/live" className="db-nav-btn">
          <i className="fa fa-video"></i> Live Sessions
        </Link>
      </nav>
      <div className="db-sidebar-footer">
        <div className="db-sidebar-user">
          <div className="db-sidebar-avatar">{initials}</div>
          <div>
            <div style={{ fontSize: 13, color: "#fff", fontWeight: 600 }}>{user?.name}</div>
            <div style={{ fontSize: 11, color: "rgba(255,255,255,0.45)" }}>{user?.email}</div>
          </div>
        </div>
        <button className="db-logout-btn" onClick={onLogout}>
          <i className="fa fa-sign-out-alt"></i> Logout
        </button>
      </div>
    </div>
  )
}

/* ══════════════════════════════════════════
   MAIN DASHBOARD
═══════════════════════════════════════════ */
const Dashboard = () => {
  const { user, userLogout } = useAuth()
  const navigate             = useNavigate()
  const [active, setActive]  = useState("dashboard")
  const [enrollments, setEnrollments] = useState([])
  const [loading, setLoading]         = useState(true)
  const [error, setError]             = useState("")

  const fetchEnrollments = useCallback(async () => {
    try {
      const { data } = await API.get("/enrollments/my")
      setEnrollments(data.data || [])
    } catch { setError("Failed to load your courses.") }
    finally { setLoading(false) }
  }, [])

  useEffect(() => { fetchEnrollments() }, [fetchEnrollments])

  const handleLogout = () => { userLogout(); navigate("/") }

  const stats = {
    enrolled:   enrollments.length,
    completed:  enrollments.filter(e => e.status === "completed").length,
    inProgress: enrollments.filter(e => e.status === "active" && e.percentComplete > 0).length,
  }

  return (
    <div className="db-layout">
      <Sidebar user={user} active={active} setActive={setActive} onLogout={handleLogout} />
      <div className="db-main">
        <div className="db-topbar">
          <h1>
            {active === "dashboard"     && "My Dashboard"}
            {active === "live-sessions" && "My Live Sessions"}
            {active === "profile"       && "Edit Profile"}
            {active === "payments"      && "My Payments"}
          </h1>
          <div className="db-user-info">
            <span>{user?.name}</span>
            <div className="db-avatar">
              {user?.name?.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2) || "U"}
            </div>
          </div>
        </div>

        <div className="db-content">
          {active === "dashboard" && (
            <DashboardHome
              user={user}
              enrollments={enrollments}
              stats={stats}
              loading={loading}
              error={error}
            />
          )}
          {active === "live-sessions" && (
            <MyLiveSessions />
          )}
          {active === "profile" && (
            <ProfileEdit user={user} />
          )}
          {active === "payments" && (
            <MyPayments />
          )}
        </div>
      </div>
    </div>
  )
}

/* ══════════════════════════════════════════
   DASHBOARD HOME TAB
═══════════════════════════════════════════ */
const DashboardHome = ({ user, enrollments, stats, loading, error }) => (
  <>
    <div className="db-welcome">
      <div>
        <h2>Welcome back, {user?.name?.split(" ")[0]}! 👋</h2>
        <p>Continue your trading education journey.</p>
      </div>
      <div className="db-welcome-icon">📈</div>
    </div>

    <div className="db-stats">
      <div className="db-stat-card">
        <div className="stat-icon gold"><i className="fa fa-graduation-cap"></i></div>
        <div className="stat-label">Enrolled</div>
        <div className="stat-value">{stats.enrolled}</div>
      </div>
      <div className="db-stat-card">
        <div className="stat-icon green"><i className="fa fa-check-circle"></i></div>
        <div className="stat-label">Completed</div>
        <div className="stat-value">{stats.completed}</div>
      </div>
      <div className="db-stat-card">
        <div className="stat-icon blue"><i className="fa fa-play-circle"></i></div>
        <div className="stat-label">In Progress</div>
        <div className="stat-value">{stats.inProgress}</div>
      </div>
    </div>

    {error && <div className="db-empty"><p>{error}</p></div>}

    <div className="db-section-title"><i className="fa fa-book"></i> My Courses</div>

    {loading ? (
      <div className="db-loading"><i className="fa fa-spinner fa-spin"></i><span>Loading…</span></div>
    ) : enrollments.length === 0 ? (
      <div className="db-empty">
        <i className="fa fa-folder-open"></i>
        <h3>No courses yet</h3>
        <p>Browse our trading programmes and enroll to start learning.</p>
        <Link to="/courses">Browse Courses</Link>
      </div>
    ) : (
      <div className="db-courses-grid">
        {enrollments.map(e => <CourseCard key={e.id} enrollment={e} />)}
      </div>
    )}

    {enrollments.length > 0 && (
      <>
        <div className="db-section-title" style={{ marginTop: 32 }}>
          <i className="fa fa-history"></i> Recent Activity
        </div>
        <div style={{ background: "#fff", borderRadius: 10, border: "1px solid rgba(0,0,0,0.06)", overflow: "hidden" }}>
          {enrollments
            .filter(e => e.lastAccessed)
            .sort((a, b) => new Date(b.lastAccessed) - new Date(a.lastAccessed))
            .slice(0, 5)
            .map(e => (
              <div key={e.id} style={{ display: "flex", alignItems: "center", gap: 14, padding: "14px 20px", borderBottom: "1px solid rgba(0,0,0,0.04)" }}>
                <i className="fa fa-play-circle" style={{ color: "#c9a227" }}></i>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: 14, color: "#1a1a1a" }}>{e.course?.title}</div>
                  <div style={{ fontSize: 12, color: "#888" }}>
                    Last accessed: {new Date(e.lastAccessed).toLocaleDateString("en-KE", { day: "numeric", month: "short", year: "numeric" })}
                  </div>
                </div>
                <Link to={`/courses/${e.course?.slug}/learn`} style={{ fontSize: 12, color: "#c9a227", fontWeight: 600, textDecoration: "none" }}>
                  Continue →
                </Link>
              </div>
            ))}
        </div>
      </>
    )}
  </>
)

/* ══════════════════════════════════════════
   COURSE CARD
═══════════════════════════════════════════ */
const CourseCard = ({ enrollment }) => {
  const course  = enrollment.course
  const percent = enrollment.percentComplete || 0
  const totalLessons = course?.modules?.reduce((s, m) => s + (m.lessons?.length || 0), 0) || 0

  return (
    <div className="db-course-card">
      <div className="db-course-thumb">
        {course?.thumbnail
          ? <img src={course.thumbnail} alt={course.title} />
          : <div className="db-course-thumb-placeholder"><i className="fa fa-chart-line"></i></div>
        }
        {enrollment.status === "completed" && (
          <div className="db-course-badge completed">✅ Completed</div>
        )}
      </div>
      <div className="db-course-info">
        <h4>{course?.title}</h4>
        <div className="db-course-meta">
          <i className="fa fa-user-tie"></i> {course?.instructor} &nbsp;·&nbsp;
          <i className="fa fa-list"></i> {totalLessons} lessons &nbsp;·&nbsp;
          <i className="fa fa-clock"></i> {course?.duration}
        </div>
        <div className="db-progress-bar-wrap">
          <div className="db-progress-label">
            <span>Progress</span>
            <span>{percent}%</span>
          </div>
          <div className="db-progress-bar">
            <div className="db-progress-fill" style={{ width: `${percent}%` }}></div>
          </div>
        </div>
        <Link to={`/courses/${course?.slug}/learn`} className="db-continue-btn">
          {percent === 0 ? "Start Learning" : percent === 100 ? "Review Course" : "Continue Learning"}
          <i className="fa fa-arrow-right"></i>
        </Link>
        {enrollment.certificateId && (
          <div className="db-cert-badge">
            <i className="fa fa-certificate"></i> Certificate ID: {enrollment.certificateId.slice(0, 8)}…
          </div>
        )}
      </div>
    </div>
  )
}

/* ══════════════════════════════════════════
   PROFILE EDIT TAB
═══════════════════════════════════════════ */
const ProfileEdit = ({ user: initialUser }) => {
  const { user, userLogout } = useAuth()
  const [form, setForm]     = useState({
    name:  user?.name  || "",
    phone: user?.phone || "",
    email: user?.email || "",
  })
  const [pwForm, setPwForm] = useState({ currentPassword: "", newPassword: "", confirm: "" })
  const [saving, setSaving]   = useState(false)
  const [savingPw, setSavingPw] = useState(false)
  const [msg, setMsg]         = useState("")
  const [msgPw, setMsgPw]     = useState("")
  const [errPw, setErrPw]     = useState("")

  const handleChange = e => setForm({ ...form, [e.target.name]: e.target.value })

  const handleSave = async (e) => {
    e.preventDefault()
    setSaving(true); setMsg("")
    try {
      await API.patch("/users/me", { name: form.name, phone: form.phone })
      setMsg("✅ Profile updated successfully!")
      // Update displayed name immediately
      window.dispatchEvent(new Event("storage"))
    } catch (err) {
      setMsg("❌ " + (err.response?.data?.message || "Update failed."))
    } finally { setSaving(false) }
  }

  const handlePwSave = async (e) => {
    e.preventDefault()
    setMsgPw(""); setErrPw("")
    if (pwForm.newPassword.length < 6) return setErrPw("Password must be at least 6 characters.")
    if (pwForm.newPassword !== pwForm.confirm) return setErrPw("Passwords do not match.")
    setSavingPw(true)
    try {
      await API.patch("/users/me/password", {
        currentPassword: pwForm.currentPassword,
        newPassword: pwForm.newPassword,
      })
      setMsgPw("✅ Password changed successfully!")
      setPwForm({ currentPassword: "", newPassword: "", confirm: "" })
    } catch (err) {
      setErrPw(err.response?.data?.message || "Password change failed.")
    } finally { setSavingPw(false) }
  }

  return (
    <div className="profile-page">

      {/* ── Profile info ── */}
      <div className="profile-card">
        <div className="profile-avatar-section">
          <div className="profile-avatar-big">
            {user?.name?.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2) || "U"}
          </div>
          <div>
            <h3>{user?.name}</h3>
            <p>{user?.email}</p>
            <span className="profile-badge">Student</span>
          </div>
        </div>
      </div>

      {/* ── Edit personal details ── */}
      <div className="profile-section">
        <h4><i className="fa fa-user"></i> Personal Information</h4>
        {msg && (
          <div className={`profile-msg ${msg.startsWith("✅") ? "success" : "error"}`}>{msg}</div>
        )}
        <form onSubmit={handleSave} className="profile-form">
          <div className="profile-form-row">
            <div className="profile-field">
              <label>Full Name *</label>
              <input
                name="name" type="text" value={form.name}
                onChange={handleChange} required
                placeholder="Your full name"
              />
            </div>
            <div className="profile-field">
              <label>Email Address</label>
              <input
                type="email" value={form.email} disabled
                style={{ opacity: 0.5, cursor: "not-allowed" }}
              />
              <small>Email cannot be changed</small>
            </div>
          </div>
          <div className="profile-form-row">
            <div className="profile-field">
              <label>Phone Number</label>
              <input
                name="phone" type="tel" value={form.phone}
                onChange={handleChange}
                placeholder="+254 7XX XXX XXX"
              />
            </div>
          </div>
          <button type="submit" className="profile-save-btn" disabled={saving}>
            {saving ? <><i className="fa fa-spinner fa-spin"></i> Saving…</> : <><i className="fa fa-save"></i> Save Changes</>}
          </button>
        </form>
      </div>

      {/* ── Change password ── */}
      <div className="profile-section">
        <h4><i className="fa fa-lock"></i> Change Password</h4>
        {errPw && <div className="profile-msg error">{errPw}</div>}
        {msgPw && <div className="profile-msg success">{msgPw}</div>}
        <form onSubmit={handlePwSave} className="profile-form">
          <div className="profile-field">
            <label>Current Password</label>
            <input
              type="password" value={pwForm.currentPassword}
              onChange={e => setPwForm({ ...pwForm, currentPassword: e.target.value })}
              required placeholder="Enter current password"
            />
          </div>
          <div className="profile-form-row">
            <div className="profile-field">
              <label>New Password</label>
              <input
                type="password" value={pwForm.newPassword}
                onChange={e => setPwForm({ ...pwForm, newPassword: e.target.value })}
                required placeholder="Min. 6 characters"
              />
            </div>
            <div className="profile-field">
              <label>Confirm New Password</label>
              <input
                type="password" value={pwForm.confirm}
                onChange={e => setPwForm({ ...pwForm, confirm: e.target.value })}
                required placeholder="Repeat new password"
              />
            </div>
          </div>
          <button type="submit" className="profile-save-btn" disabled={savingPw}>
            {savingPw ? <><i className="fa fa-spinner fa-spin"></i> Updating…</> : <><i className="fa fa-key"></i> Change Password</>}
          </button>
        </form>
      </div>

    </div>
  )
}

/* ══════════════════════════════════════════
   MY LIVE SESSIONS TAB
═══════════════════════════════════════════ */
const MyLiveSessions = () => {
  const [bookings, setBookings] = useState([])
  const [loading,  setLoading]  = useState(true)

  useEffect(() => {
    API.get("/live-sessions/my-bookings")
      .then(res => setBookings(res.data.data || []))
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [])

  const fmtDate = (d) => new Date(d).toLocaleString("en-KE", {
    weekday: "short", day: "numeric", month: "short",
    year: "numeric", hour: "2-digit", minute: "2-digit",
  })

  const statusStyle = {
    upcoming:  { bg: "rgba(201,162,39,0.1)",  color: "#a07c10",  label: "📅 Upcoming" },
    live:      { bg: "rgba(229,62,62,0.1)",   color: "#e53e3e",  label: "🔴 Live Now" },
    completed: { bg: "rgba(0,0,0,0.06)",       color: "#666",     label: "✅ Completed" },
    cancelled: { bg: "rgba(220,53,69,0.1)",   color: "#dc3545",  label: "Cancelled" },
  }

  if (loading) return <div className="db-loading"><i className="fa fa-spinner fa-spin"></i><span>Loading…</span></div>

  return (
    <div>
      <div className="db-section-title"><i className="fa fa-video"></i> My Booked Sessions</div>

      {bookings.length === 0 ? (
        <div className="db-empty">
          <i className="fa fa-calendar-times"></i>
          <h3>No sessions booked</h3>
          <p>Browse our live trading sessions and book your spot.</p>
          <Link to="/live">View Live Sessions</Link>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          {bookings.map(booking => {
            const s   = booking.session
            const st  = statusStyle[s?.status] || statusStyle.upcoming
            const now = new Date()
            const sessionTime = new Date(s?.sessionDate)
            const isUpcoming  = s?.status === "upcoming"
            const isLive      = s?.status === "live"

            return (
              <div key={booking.id} style={{
                background: "#fff", borderRadius: 10,
                border: "1px solid rgba(201,162,39,0.12)",
                padding: "18px 20px",
                boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
              }}>
                <div style={{ display: "flex", alignItems: "flex-start", gap: 16, flexWrap: "wrap" }}>
                  {/* Icon */}
                  <div style={{
                    width: 48, height: 48, borderRadius: "50%", flexShrink: 0,
                    background: "linear-gradient(135deg,#a07c10,#c9a227)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}>
                    <i className="fa fa-video" style={{ color: "#fff", fontSize: 18 }}></i>
                  </div>

                  {/* Details */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 4 }}>
                      <strong style={{ fontSize: 15, color: "#1a1a1a" }}>{s?.title}</strong>
                      <span style={{
                        background: st.bg, color: st.color,
                        fontSize: 11, fontWeight: 700, padding: "2px 10px",
                        borderRadius: 20,
                      }}>{st.label}</span>
                    </div>
                    <div style={{ fontSize: 13, color: "#888", marginBottom: 6 }}>
                      <i className="fa fa-calendar" style={{ color: "#c9a227", marginRight: 6 }}></i>
                      {fmtDate(s?.sessionDate)}
                      <span style={{ margin: "0 8px" }}>·</span>
                      <i className="fa fa-clock" style={{ color: "#c9a227", marginRight: 6 }}></i>
                      {s?.duration} min
                    </div>

                    {/* Meeting info if available */}
                    {s?.meetingUrl && (isLive || isUpcoming) && (
                      <div style={{
                        background: "rgba(201,162,39,0.06)",
                        border: "1px solid rgba(201,162,39,0.2)",
                        borderRadius: 6, padding: "8px 12px", marginTop: 8,
                        fontSize: 13,
                      }}>
                        <span style={{ fontWeight: 700, color: "#a07c10", marginRight: 8 }}>
                          <i className="fa fa-link" style={{ marginRight: 4 }}></i>Meeting:
                        </span>
                        <a href={s.meetingUrl} target="_blank" rel="noreferrer"
                          style={{ color: "#c9a227", wordBreak: "break-all" }}>
                          {s.meetingUrl}
                        </a>
                        {s.meetingId && (
                          <span style={{ display: "block", color: "#888", fontSize: 12, marginTop: 4 }}>
                            ID: {s.meetingId}
                            {s.meetingPassword && ` · Password: ${s.meetingPassword}`}
                          </span>
                        )}
                      </div>
                    )}

                    {/* Recording */}
                    {s?.status === "completed" && s?.recordingUrl && (
                      <a href={s.recordingUrl} target="_blank" rel="noreferrer"
                        style={{
                          display: "inline-flex", alignItems: "center", gap: 6,
                          marginTop: 8, padding: "7px 16px", borderRadius: 6,
                          background: "linear-gradient(135deg,#6f42c1,#9b59b6)",
                          color: "#fff", fontSize: 13, fontWeight: 700, textDecoration: "none",
                        }}>
                        <i className="fa fa-play-circle"></i> Watch Recording
                      </a>
                    )}
                  </div>

                  {/* Join button */}
                  {isLive && s?.meetingUrl && (
                    <a href={s.meetingUrl} target="_blank" rel="noreferrer"
                      className="db-continue-btn"
                      style={{ background: "#e53e3e", borderColor: "#e53e3e", animation: "none" }}>
                      <i className="fa fa-video"></i> Join Now
                    </a>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      )}

      <div style={{ marginTop: 20 }}>
        <Link to="/live" className="db-nav-btn" style={{ width: "auto", display: "inline-flex" }}>
          <i className="fa fa-plus-circle"></i> Book More Sessions
        </Link>
      </div>
    </div>
  )
}

/* ══════════════════════════════════════════
   MY PAYMENTS TAB
═══════════════════════════════════════════ */
const MyPayments = () => {
  const [payments, setPayments] = useState([])
  const [loading, setLoading]   = useState(true)

  useEffect(() => {
    API.get("/payments/my")
      .then(res => setPayments(res.data.data || []))
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [])

  if (loading) return <div className="db-loading"><i className="fa fa-spinner fa-spin"></i><span>Loading…</span></div>

  return (
    <div>
      <div className="db-section-title"><i className="fa fa-credit-card"></i> Payment History</div>
      {payments.length === 0 ? (
        <div className="db-empty">
          <i className="fa fa-receipt"></i>
          <h3>No payments yet</h3>
          <p>Your payment history will appear here after you enroll in a course.</p>
        </div>
      ) : (
        <div style={{ background: "#fff", borderRadius: 10, border: "1px solid rgba(0,0,0,0.06)", overflow: "hidden" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
            <thead>
              <tr style={{ background: "#f8f8f8" }}>
                <th style={th}>Course</th>
                <th style={th}>Amount</th>
                <th style={th}>Method</th>
                <th style={th}>Status</th>
                <th style={th}>Date</th>
              </tr>
            </thead>
            <tbody>
              {payments.map(p => (
                <tr key={p.id} style={{ borderBottom: "1px solid rgba(0,0,0,0.04)" }}>
                  <td style={td}>{p.course?.title}</td>
                  <td style={{ ...td, fontWeight: 700 }}>KES {Number(p.amount).toLocaleString()}</td>
                  <td style={td}>
                    <span style={{ textTransform: "uppercase", fontSize: 12, fontWeight: 600 }}>{p.method}</span>
                  </td>
                  <td style={td}>
                    <span className={`badge badge-${p.status}`}>{p.status}</span>
                  </td>
                  <td style={{ ...td, color: "#888", fontSize: 12 }}>
                    {new Date(p.createdAt).toLocaleDateString("en-KE", { day: "numeric", month: "short", year: "numeric" })}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
const th = { padding: "12px 16px", textAlign: "left", fontWeight: 700, fontSize: 12, color: "#555", textTransform: "uppercase", letterSpacing: "0.5px" }
const td = { padding: "13px 16px", color: "#333", verticalAlign: "middle" }

export default Dashboard
